import React from "react";
import PropTypes from "prop-types";

// "neutral" is the resting fill: a healthy budget's bar should be readable without also being
// a green signal competing for attention. Semantic colour is reserved for states that warrant
// intervention, so the eye can scan a column of bars and only stop where it matters.
const TONE = { danger: "bg-danger", warning: "bg-warning", success: "bg-success", neutral: "bg-text-3" };

// When spend (or its projection) runs past the cap, a bar clamped at 100% hides HOW FAR
// over it is — exactly the signal an over-budget reader needs. So the track rescales to fit
// the overage (with a little headroom): the cap mark moves left of the right edge and the
// fill visibly extends past it. Under the cap, the scale stays 0–100 so on-track bars are
// unchanged.
export default function ThresholdBar({ nowPct, marks = {}, tone = "success", projectedPct, height = 8 }) {
  const now = Math.max(0, nowPct || 0);
  const proj = projectedPct != null ? Math.max(0, projectedPct) : null;
  const over = now > 100 || (proj != null && proj > 100);
  const scale = over ? Math.max(now, proj || 0, 100) * 1.08 : 100;
  const pos = (v) => (v == null ? null : Math.min(100, (v / scale) * 100));
  const fill = pos(now);
  const overhang = Math.max(2, Math.round(height * 0.35));
  const Tick = ({ at, strong }) =>
    at == null ? null : (
      <span
        data-testid="tb-tick"
        className={strong ? "bg-text" : "bg-text-3"}
        style={{ position: "absolute", top: -overhang, bottom: -overhang, left: `${at}%`, width: 2 }}
      />
    );
  return (
    // Width is inline: Tailwind's width scale is overridden in this repo, so `w-full` is a no-op
    // and the track would collapse to nothing / to its content.
    <span className="relative block overflow-visible rounded-full bg-surface-2" style={{ height, width: "100%" }}>
      <span data-testid="tb-fill" className={`block rounded-full ${TONE[tone]}`} style={{ width: `${fill}%`, height }} />
      <Tick at={pos(marks.soft)} strong={false} />
      <Tick at={pos(marks.hard)} strong />
      {proj != null && proj > now ? (
        <span data-testid="tb-proj" className="bg-warning" style={{ position: "absolute", top: -overhang, bottom: -overhang, left: `${pos(proj)}%`, width: 2, opacity: 0.7 }} />
      ) : null}
    </span>
  );
}
ThresholdBar.propTypes = {
  nowPct: PropTypes.number,
  marks: PropTypes.shape({ soft: PropTypes.number, hard: PropTypes.number }),
  tone: PropTypes.oneOf(["danger", "warning", "success", "neutral"]),
  projectedPct: PropTypes.number,
  height: PropTypes.number,
};
